export const hoursOf = (entry) => Number(entry?.time) || 0;
export const orderIdOf = (entry) => entry?.work_order?.id ?? entry?.work_order_id ?? null;
export const activityIdOf = (entry) => entry?.project_activity?.id ?? entry?.project_activity_id ?? null;
export const projectIdOf = (entry) => entry?.project?.id ?? entry?.project_id ?? null;
export const departmentIdOf = (entry) =>
  entry?.user?.department?.id ?? entry?.user?.department_id ?? null;
export const supplierInvoiceIdOf = (entry) => entry?.billing?.supplierinvoice_id ?? null;

export function uncoveredSupplierHoursOf(entry) {
  if (!entry?.billing?.hired) return 0;
  const hours = hoursOf(entry);
  const uncovered = Number(entry.billing.estimated_uncovered_hours);
  if (entry.billing.estimated_uncovered_hours != null && Number.isFinite(uncovered)) {
    return Math.max(0, uncovered);
  }
  if (supplierInvoiceIdOf(entry) == null) return hours;
  if (entry.billing.needs_supplier_invoice === false) return 0;
  const covered = Number(entry.billing.invoice_hours);
  if (Number.isFinite(covered)) return Math.max(0, hours - covered);
  return entry.billing.needs_supplier_invoice ? hours : 0;
}

export function statusOf(entry) {
  if (entry?.approved) return "approved";
  if (entry?.not_approved) return "rejected";
  if (entry?.processed) return "processed";
  return "pending";
}

export function summarizeProjectHours(entries) {
  const rows = Array.isArray(entries) ? entries : [];
  const total = rows.reduce((sum, entry) => sum + hoursOf(entry), 0);
  const pending = rows.filter((entry) => statusOf(entry) === "pending");
  const withoutOrder = rows.filter((entry) => orderIdOf(entry) == null);
  const approved = rows.filter((entry) => statusOf(entry) === "approved");
  const supplier = rows.filter((entry) => entry?.billing?.hired);
  return {
    count: rows.length,
    total,
    approved: approved.reduce((sum, entry) => sum + hoursOf(entry), 0),
    pending: pending.reduce((sum, entry) => sum + hoursOf(entry), 0),
    pendingCount: pending.length,
    withoutOrder: withoutOrder.reduce((sum, entry) => sum + hoursOf(entry), 0),
    withoutOrderCount: withoutOrder.length,
    supplier: supplier.reduce((sum, entry) => sum + hoursOf(entry), 0),
    supplierUnlinked: supplier.reduce((sum, entry) => sum + uncoveredSupplierHoursOf(entry), 0),
    people: new Set(rows.map((entry) => entry?.user?.id).filter((id) => id != null)).size,
  };
}

export const PROJECT_TIME_LINK_VALUES = ["order", "no-order", "invoice", "no-invoice"];

/** Whether an hour matches one value of the "Link" facet. */
export function matchesLink(entry, link) {
  switch (link) {
    case "order": return orderIdOf(entry) != null;
    case "no-order": return orderIdOf(entry) == null;
    case "invoice": return Boolean(entry?.billing?.hired) && supplierInvoiceIdOf(entry) != null;
    case "no-invoice": return uncoveredSupplierHoursOf(entry) > 0;
    default: return true;
  }
}

/** Accepts the multi-select `links` array (and the legacy single `link` string). */
export function linkFilterValues(filters = {}) {
  if (Array.isArray(filters.links)) return filters.links.filter(Boolean).map(String);
  return filters.link ? [String(filters.link)] : [];
}

/**
 * Every facet is multi-select: values within one facet are OR-ed,
 * different facets (and the free-text query) are AND-ed.
 */
export function filterProjectHours(entries, filters = {}) {
  const locale = filters.locale || "nb-NO";
  const links = linkFilterValues(filters);
  const q = String(filters.query || "").trim().toLocaleLowerCase(locale);
  return (entries || []).filter((entry) => {
    const orderId = orderIdOf(entry);
    const activityId = activityIdOf(entry);
    if (filters.userIds?.length && !filters.userIds.includes(String(entry?.user?.id ?? "none"))) return false;
    if (filters.projectIds?.length && !filters.projectIds.includes(String(projectIdOf(entry) ?? "none"))) return false;
    if (filters.departmentIds?.length && !filters.departmentIds.includes(String(departmentIdOf(entry) ?? "none"))) return false;
    if (filters.activityIds?.length && !filters.activityIds.includes(String(activityId ?? "none"))) return false;
    if (filters.orderIds?.length && !filters.orderIds.includes(orderId == null ? "none" : String(orderId))) return false;
    if (filters.statuses?.length && !filters.statuses.includes(statusOf(entry))) return false;
    if (filters.types?.length && !filters.types.includes(entry?.billing?.hired ? "supplier" : "employee")) return false;
    if (links.length && !links.some((link) => matchesLink(entry, link))) return false;
    if (q) {
      const haystack = [entry?.description, entry?.process_note, entry?.user?.displayTitle,
        entry?.work_order?.displayTitle, entry?.work_order?.number,
        entry?.project_activity?.display_title, entry?.project_activity?.title]
        .filter(Boolean).join(" ").toLocaleLowerCase(locale);
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function countProjectTimeFacets(entries, filters = {}) {
  const without = (key) => filterProjectHours(entries, { ...filters, [key]: [], ...(key === "links" ? { link: "" } : {}) });
  const countBy = (rows, keyOf) => rows.reduce((counts, entry) => {
    const key = String(keyOf(entry) ?? "none");
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
  const linkRows = without("links");
  return {
    users: countBy(without("userIds"), (entry) => entry?.user?.id),
    projects: countBy(without("projectIds"), projectIdOf),
    departments: countBy(without("departmentIds"), departmentIdOf),
    activities: countBy(without("activityIds"), activityIdOf),
    orders: countBy(without("orderIds"), orderIdOf),
    statuses: countBy(without("statuses"), statusOf),
    types: countBy(without("types"), (entry) => entry?.billing?.hired ? "supplier" : "employee"),
    links: Object.fromEntries(PROJECT_TIME_LINK_VALUES.map((link) =>
      [link, linkRows.filter((entry) => matchesLink(entry, link)).length])),
  };
}

export function groupProjectHours(entries, groupBy = "order", labels = {}) {
  const groups = new Map();
  (entries || []).forEach((entry) => {
    let id;
    let title;
    switch (groupBy) {
      case "user":
        id = entry?.user?.id ?? "none";
        title = entry?.user?.displayTitle || labels.unknownEmployee || "Ukjent ansatt";
        break;
      case "project":
        id = projectIdOf(entry) ?? "none";
        title = entry?.project?.displayTitle || entry?.project?.name || entry?.project?.title
          || labels.withoutProject || "Uten prosjekt";
        break;
      case "activity":
        id = activityIdOf(entry) ?? "none";
        title = entry?.project_activity?.display_title || entry?.project_activity?.title || labels.withoutActivity || "Uten aktivitet";
        break;
      case "date":
        id = entry?.date || "none";
        title = entry?.date || labels.withoutDate || "Uten dato";
        break;
      case "status":
        id = statusOf(entry);
        title = ({ approved: labels.approved || "Godkjent", rejected: labels.rejected || "Avvist",
          processed: labels.processed || "Behandlet", pending: labels.pending || "Til godkjenning" })[id];
        break;
      case "order":
      default:
        id = orderIdOf(entry) ?? "none";
        title = entry?.work_order?.displayTitle || entry?.work_order?.title ||
          (id === "none" ? labels.withoutOrder || "Uten ordre" : `${labels.order || "Ordre"} #${id}`);
    }
    const key = String(id);
    if (!groups.has(key)) groups.set(key, { key, title, entries: [], hours: 0 });
    const group = groups.get(key);
    group.entries.push(entry);
    group.hours += hoursOf(entry);
  });
  return [...groups.values()].sort((a, b) => a.title.localeCompare(b.title, labels.locale || "nb-NO"));
}

export function projectHoursCsv(entries, { locale = "nb-NO", labels = {}, includeBilling = false } = {}) {
  const quote = (value) => {
    const raw = String(value ?? "");
    // Descriptions and names are user content. Prevent spreadsheet formula execution.
    const safe = /^[=+\-@]/.test(raw.trimStart()) ? `'${raw}` : raw;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const header = [labels.date || "Dato", labels.employee || "Ansatt",
    ...(includeBilling ? [labels.type || "Type"] : []),
    labels.activity || "Aktivitet", labels.order || "Ordre",
    ...(includeBilling ? [labels.supplierInvoice || "Leverandørfaktura"] : []),
    labels.hours || "Timer", labels.status || "Status", labels.description || "Beskrivelse"];
  const statusLabels = { approved: labels.approved || "Godkjent", rejected: labels.rejected || "Avvist",
    processed: labels.processed || "Behandlet", pending: labels.pending || "Til godkjenning" };
  const lines = (entries || []).map((entry) => [
    entry.date, entry.user?.displayTitle,
    ...(includeBilling ? [entry.billing?.hired ? labels.supplier || "Underleverandør" : labels.employee || "Ansatt"] : []),
    entry.project_activity?.display_title || entry.project_activity?.title,
    entry.work_order?.number || entry.work_order?.displayTitle,
    ...(includeBilling ? [supplierInvoiceIdOf(entry)] : []),
    hoursOf(entry).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 }),
    statusLabels[statusOf(entry)], entry.description,
  ].map(quote).join(";"));
  return `\uFEFF${[header.map(quote).join(";"), ...lines].join("\r\n")}`;
}
